import { Button, Card, Col, notification, Popconfirm, Row, Dropdown, Modal, Select } from "antd";
import React, { useState } from "react";
import { DotsThree, CaretLeft } from "@phosphor-icons/react";
import styled from "styled-components";
import { colors } from "../../styles/theme";
import { ProductMainData } from "../organisms/product-main-data/ProductMainData";
import { useUnsavedChangesWarning } from "../../hooks/useUnsavedChangesWarning";
import ProductEditBar from "../molecules/product-edit-bar/ProductEditBar";
import { useEditProductContext } from "../../contexts/edit-product-context/useEditProductContext";
import { ProductVisibilityData } from "../organisms/product-visibility-data/ProductVisibilityData";
import { ProductLabelsData } from "../organisms/product-labels-data/ProductLabelsData";
import { VariantsList } from "../organisms/variants-list/VariantsList";
import { ExtensionConfigurator } from "../organisms/extensions-configurator/ExtensionConfigurator";
import { useMutation, useQuery } from "react-query";
import { removeProduct, getProductCategories, changeProductCategory, duplicateProduct } from "../../service";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { Copy } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

export const Section = styled(Card)`
  background: ${({ theme }) => theme.colors.surface};
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  margin-bottom: 16px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-divider);
`;

const BreadcrumbWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  gap: 8px;
  font-size: 14px;
  color: var(--color-text);
`;

const BreadcrumbItem = styled.span`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
`;

const BreadcrumbSeparator = styled.span`
  color: var(--color-text-secondary);
  flex-shrink: 0;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 4px;
`;

const EditProduct: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { productID } = useParams();
  const { unsavedChanges, name, productCategory, setProductCategory } = useEditProductContext();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  useUnsavedChangesWarning(unsavedChanges);

  const handleBack = () => {
    navigate('/menu', {
      state: {
        scrollToCategoryId: productCategory?.id,
        scrollToProductId: parseInt(productID || '0'),
      },
    });
  };

  const { data: categories = [] } = useQuery(
    ["/product-categories"],
    () => getProductCategories(),
    {
      onSuccess: (data) => {
        if (productCategory) {
          setSelectedCategory(productCategory.id);
        }
      },
    }
  );

  const { mutateAsync: changeCategoryMutation, isLoading: isChangingCategory } = useMutation(
    changeProductCategory,
    {
      onSuccess: (data) => {
        setProductCategory(data.productCategory);
        queryClient.invalidateQueries(["/product", productID]);
        notification.success({
          message: t("editProductPage.categoryChangedSuccess"),
          placement: "bottomRight",
        });
        setIsCategoryModalOpen(false);
      },
      onError: () => {
        notification.error({
          message: t("editProductPage.errorTitle"),
          description: t("editProductPage.categoryChangeError"),
          placement: "bottomRight",
        });
      },
    }
  );

  const {
    mutateAsync: removeProductMutateAsync,
    isLoading: removeProductMutateAsyncIsLoading,
  } = useMutation(removeProduct, {
    onSuccess: (data) => {
      navigate("/menu");
      notification.success({
        message: t("editProductPage.productRemovedSuccess"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });
    },
    onError: () => {
      notification.error({
        message: t("editProductPage.errorTitle"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
    },
  });

  const handleDuplicateProduct = async () => {
    if (!productID) return;

    setIsDuplicating(true);

    try {
      const duplicatedProduct = await duplicateProduct(parseInt(productID));

      // Krótkie opóźnienie dla efektu wizualnego
      setTimeout(() => {
        queryClient.invalidateQueries(["/product-categories"]);
        notification.success({
          message: t("editProductPage.productDuplicatedSuccess"),
          description: t("editProductPage.productDuplicatedDescription", { name: duplicatedProduct.name }),
          placement: "bottomRight",
        });
        setIsDuplicating(false);
        // Nawiguj do zduplikowanego produktu
        navigate(`/edit-product/${duplicatedProduct.id}`);
      }, 1000);
    } catch (error) {
      setIsDuplicating(false);
      notification.error({
        message: t("editProductPage.errorTitle"),
        description: t("editProductPage.productDuplicateError"),
        placement: "bottomRight",
      });
    }
  };

  const menuItems = [
    {
      key: "change-category",
      label: t("editProductPage.changeCategoryMenuItem"),
      onClick: () => setIsCategoryModalOpen(true),
    },
    {
      key: "duplicate-product",
      label: t("editProductPage.duplicateProductMenuItem"),
      icon: <Copy size={16} />,
      onClick: handleDuplicateProduct,
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        paddingBottom: "40px",
        position: "relative",
      }}
    >
      {/* Efekt frozen podczas duplikowania */}
      {isDuplicating && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
            pointerEvents: "all",
            cursor: "wait",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 16, color: "var(--color-text-secondary)" }}>{t("editProductPage.duplicatingProduct")}</div>
          </div>
        </div>
      )}
      <HeaderContainer>
        <BackButton
          type="text"
          icon={<CaretLeft size={20} weight="bold" />}
          onClick={handleBack}
        />
        <BreadcrumbWrapper>
          <BreadcrumbItem title={productCategory?.name || t("editProductPage.loading")}>
            {productCategory?.name || t("editProductPage.loading")}
          </BreadcrumbItem>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbItem title={name || t("editProductPage.loading")}>
            {name || t("editProductPage.loading")}
          </BreadcrumbItem>
        </BreadcrumbWrapper>
        <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={["click"]}>
          <MenuButton type="text" icon={<DotsThree size={24} weight="bold" />} />
        </Dropdown>
      </HeaderContainer>

      <Row
        gutter={16}
        style={{
          padding: "12px",
        }}
      >
        <Col xs={24} lg={18}>
          <Section title={t("editProductPage.basicInfoSection")}>
            <ProductMainData />
          </Section>
          <VariantsList />
          <Section title={t("editProductPage.extrasSection")}>
            <ExtensionConfigurator />
          </Section>
        </Col>
        <Col xs={24} lg={6}>
          <Section title={t("editProductPage.visibilitySection")}>
            <ProductVisibilityData />
          </Section>
          {/* <Section title="Etykiety">
            <ProductLabelsData />
          </Section> */}
          <Popconfirm
            title={t("editProductPage.removeProductConfirmTitle")}
            description={t("editProductPage.removeProductConfirmDescription")}
            onConfirm={() => {
              removeProductMutateAsync({ productID });
            }}
            okButtonProps={{ loading: removeProductMutateAsyncIsLoading }}
            okText={t("editProductPage.yes")}
            cancelText={t("editProductPage.no")}
          >
            <Button
              danger
              style={{
                width: "100%",
              }}
              disabled={unsavedChanges}
              loading={removeProductMutateAsyncIsLoading}
            >
              {t("editProductPage.removeProductButton")}
            </Button>
          </Popconfirm>
        </Col>
      </Row>
      {unsavedChanges && <ProductEditBar />}

      <Modal
        title={t("editProductPage.changeCategoryModalTitle")}
        open={isCategoryModalOpen}
        onCancel={() => setIsCategoryModalOpen(false)}
        onOk={async () => {
          if (!selectedCategory || selectedCategory === productCategory?.id) {
            notification.warning({
              message: t("editProductPage.selectDifferentCategoryTitle"),
              description: t("editProductPage.selectDifferentCategoryDescription"),
              placement: "bottomRight",
            });
            return;
          }
          await changeCategoryMutation({
            productId: parseInt(productID || "0"),
            newCategoryId: selectedCategory,
          });
        }}
        okText={t("editProductPage.confirm")}
        cancelText={t("editProductPage.cancel")}
        confirmLoading={isChangingCategory}
      >
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8 }}>
            {t("editProductPage.selectCategoryLabel")}
          </label>
          <Select
            style={{ width: "100%" }}
            value={selectedCategory}
            onChange={(value) => setSelectedCategory(value)}
            placeholder={t("editProductPage.selectCategoryPlaceholder")}
            options={categories.map((category: any) => ({
              label: category.name,
              value: category.id,
            }))}
          />
        </div>
      </Modal>
    </div>
  );
};

export default EditProduct;
